/**
 * Provider React qui orchestre le lifecycle du session store
 *
 * Charge la session au mount (si authentifie), ecoute les evenements
 * de modification de permissions/subscriptions, et gere le refresh
 * au focus de fenetre si les donnees sont stale (> 10 minutes).
 *
 * Garantie : quand les children montent, la session est READY.
 * Aucun composant enfant n'a besoin de gerer un etat loading pour les permissions.
 *
 * @module SessionProvider
 * @author Equipe Brydge
 * @since 3.0.0
 */

import {useEffect, useRef, useMemo, useState, useCallback} from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import useSessionStore from 'packages/common/stores/sessionStore';
import {useAuthContext} from 'packages/common/hooks/useAuthContext';
import {useAdminAccess} from 'packages/common/hooks/useAdminAccess';
import {getSelectedCompanyId, clearSelectedCompany} from 'packages/common/utils/companyStorage';
import {APP_EVENTS, createEventListeners} from 'packages/common/utils/appEventSync';
import SplashScreen from 'packages/common/components/loading-screen/splash-screen';
import {SESSION_STATUS} from 'packages/common/stores/sessionStatus';

// Au retour sur l'onglet, la session est revalidée si elle date de plus de
// 2 minutes : l'état des modules/permissions reste quasi temps réel sans
// marteler l'API (le garde _lastRefresh absorbe les événements focus doublés).
const STALE_THRESHOLD = 2 * 60 * 1000;

// Cadence de reprise quand le serveur ne répond pas. Une tablette en salle ne
// doit pas attendre qu'on appuie sur un bouton pour retrouver sa session après
// un déploiement : la requête de session est légère, on retente calmement.
const RECONNECT_INTERVAL = 5 * 1000;

// Délai avant d'annoncer la panne à l'utilisateur. En dessous, on garde l'écran
// de chargement habituel : une coupure de quelques secondes se répare toute
// seule, et l'annoncer inquiéterait pour rien. Le message n'apparaît donc
// qu'après une dizaine de secondes d'indisponibilité réelle, typiquement un
// déploiement, jamais sur un décrochage de réseau en salle.
const RECONNECT_NOTICE_DELAY = 9 * 1000;

// Délai avant de proposer une sortie sur l'écran de reconnexion. La reprise
// automatique suffit à un déploiement, et proposer trop tôt de se déconnecter
// pousserait à quitter une session parfaitement valide. Passé ce délai, la
// panne n'a plus l'air passagère : l'utilisateur doit pouvoir repartir vers la
// page de connexion plutôt que de rester devant un écran qui tourne.
const ESCAPE_OFFER_DELAY = 60 * 1000;

/**
 * Écran plein cadre centré, partagé par les états de reprise et d'erreur.
 *
 * @param {Object} props - Propriétés du composant
 * @param {Object} props.children - Contenu affiché au centre
 * @return {JSX.Element} Écran centré
 */
function StatusScreen({children}) {
  return (
    <Box
      sx={{
        right: 0,
        width: 1,
        bottom: 0,
        height: 1,
        zIndex: 9998,
        display: 'flex',
        flexDirection: 'column',
        position: 'absolute',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        gap: 3,
        px: 3,
        textAlign: 'center',
      }}
    >
      {children}
    </Box>
  );
}

export default function SessionProvider({children}) {
  const {isAuthenticated, logout} = useAuthContext();
  const {isAdminAccessMode, targetCompany} = useAdminAccess();

  const status = useSessionStore((s) => s.status);
  const activeCompanyId = useSessionStore((s) => s.activeCompany?._id);
  const initialize = useSessionStore((s) => s.initialize);
  const reset = useSessionStore((s) => s.reset);
  const refreshSession = useSessionStore((s) => s.refreshSession);

  const initializingRef = useRef(false);
  const [noticeVisible, setNoticeVisible] = useState(false);
  const [escapeVisible, setEscapeVisible] = useState(false);

  // Resolution du companyId initial
  const initialCompanyId = useMemo(() => {
    if (isAdminAccessMode && targetCompany?.id) return targetCompany.id;
    const localId = getSelectedCompanyId();
    if (localId) return localId;
    return null;
  }, [isAdminAccessMode, targetCompany]);

  const handleRetry = useCallback(() => {
    if (initializingRef.current) return;
    initializingRef.current = true;
    // Vider le companyId potentiellement stale pour forcer le backend
    // a choisir la bonne company au lieu de rejouer la meme erreur
    clearSelectedCompany();
    useSessionStore.setState({status: SESSION_STATUS.IDLE, error: null});
    initialize(null).finally(() => {
      initializingRef.current = false;
    });
  }, [initialize]);

  // Initialisation au mount quand authentifie
  useEffect(() => {
    if (isAuthenticated && status === SESSION_STATUS.IDLE && !initializingRef.current) {
      initializingRef.current = true;
      initialize(initialCompanyId).finally(() => {
        initializingRef.current = false;
      });
    }
  }, [isAuthenticated, status, initialize, initialCompanyId]);

  // L'annonce de panne n'apparaît qu'au bout du délai, et disparaît dès que le
  // serveur répond de nouveau.
  useEffect(() => {
    if (status !== SESSION_STATUS.UNAVAILABLE) {
      setNoticeVisible(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => setNoticeVisible(true), RECONNECT_NOTICE_DELAY);
    return () => clearTimeout(timeoutId);
  }, [status]);

  // La sortie de secours apparaît plus tard que l'annonce, et disparaît de même
  // dès que le serveur répond.
  useEffect(() => {
    if (status !== SESSION_STATUS.UNAVAILABLE) {
      setEscapeVisible(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => setEscapeVisible(true), ESCAPE_OFFER_DELAY);
    return () => clearTimeout(timeoutId);
  }, [status]);

  // Reprise automatique tant que le serveur ne répond pas.
  //
  // L'effet reste monté pendant les tentatives : `initialize` conserve
  // délibérément le statut `unavailable` pour ne pas faire clignoter l'écran de
  // reconnexion, donc l'intervalle continue de tourner. C'est `initializingRef`
  // qui empêche deux tentatives de se superposer, et lui seul : le retirer
  // ferait partir une requête toutes les cinq secondes même quand la
  // précédente est encore en vol.
  useEffect(() => {
    if (!isAuthenticated || status !== SESSION_STATUS.UNAVAILABLE) return undefined;

    const retry = () => {
      if (initializingRef.current) return;
      initializingRef.current = true;
      initialize(initialCompanyId).finally(() => {
        initializingRef.current = false;
      });
    };

    const intervalId = setInterval(retry, RECONNECT_INTERVAL);
    window.addEventListener('online', retry);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('online', retry);
    };
  }, [isAuthenticated, status, initialize, initialCompanyId]);

  // Reset quand l'utilisateur se deconnecte
  useEffect(() => {
    if (!isAuthenticated && status !== SESSION_STATUS.IDLE) {
      reset();
    }
  }, [isAuthenticated, status, reset]);

  // Ecoute des evenements discrets pour refresh automatique
  useEffect(() => {
    if (!isAuthenticated || !activeCompanyId) return;

    const cleanups = [
      createEventListeners(activeCompanyId, APP_EVENTS.SUBSCRIPTIONS.LIST_UPDATED, refreshSession),
      createEventListeners(activeCompanyId, APP_EVENTS.USERS.LIST_UPDATED, refreshSession),
      createEventListeners(activeCompanyId, APP_EVENTS.PERMISSIONS.UPDATED, refreshSession),
      // Les détails company portent l'activation des modules (modulesActivation) :
      // un toggle dans un onglet doit rafraîchir la session des autres onglets
      createEventListeners(activeCompanyId, APP_EVENTS.COMPANY.DETAILS_UPDATED, refreshSession),
    ];

    return () => cleanups.forEach((cleanup) => cleanup());
  }, [isAuthenticated, activeCompanyId, refreshSession]);

  // Refresh au focus de fenetre si stale > 10 minutes
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const lastRefresh = useSessionStore.getState()._lastRefresh;
      if (lastRefresh && Date.now() - lastRefresh > STALE_THRESHOLD) {
        refreshSession();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, refreshSession]);

  // Le serveur ne répond plus depuis assez longtemps pour que l'utilisateur
  // mérite une explication. En deçà, il ne voit que l'écran de chargement.
  if (isAuthenticated && status === SESSION_STATUS.UNAVAILABLE && noticeVisible) {
    return (
      <StatusScreen>
        <CircularProgress />

        <Typography variant="h6" color="text.primary">
          Reconnexion en cours
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{maxWidth: 400}}>
          Le serveur ne répond pas pour le moment. Vous restez connecté et rien
          n&apos;est perdu : l&apos;affichage revient dès que la liaison est rétablie.
        </Typography>

        {escapeVisible && (
          <Button variant="outlined" onClick={logout}>
            Revenir à la connexion
          </Button>
        )}
      </StatusScreen>
    );
  }

  // Erreur averee : l'utilisateur doit agir.
  if (isAuthenticated && status === SESSION_STATUS.ERROR) {
    return (
      <StatusScreen>
        <Typography variant="h6" color="text.primary">
          Impossible de charger votre session
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{maxWidth: 400}}>
          Une erreur est survenue lors du chargement de vos données.
          Vérifiez votre connexion et réessayez.
        </Typography>

        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center'}}>
          <Button variant="contained" onClick={handleRetry}>
            Réessayer
          </Button>

          <Button variant="outlined" onClick={logout}>
            Revenir à la connexion
          </Button>
        </Box>
      </StatusScreen>
    );
  }

  // Afficher le SplashScreen pendant le chargement initial
  if (isAuthenticated && status !== SESSION_STATUS.READY) {
    return <SplashScreen />;
  }

  return children;
}
